/**
 * Musical data structures.
 */

import { stepsToMidiNoteNumbers } from "./sound";

export class Performance {
	constructor(public melodies: Melody[]) {
	}
}

export class Melody {
	constructor(public timedChords: TimedChord[],
	            public instrument: Instrument,
	            public loudnessFactor = 1) {
	}
}

export class TimedChord {
	constructor(public chord: Chord,
	            public startTime: Seconds,
	            public loudness: Loudness = 0.8) {
	}
}

export class Chord {
	constructor(public notes: Note[], //notes sorted by ascending pitch
	            public duration: Seconds) {
	}
}

export interface Note {
}

export class PitchedNote implements Note {
	constructor(public pitch: Pitch,
	            public tuning: Cents = 0) {
	}
}

export class UnpitchedNote implements Note {
	constructor(public sound: string) {
	}
}

export class Pitch {
	constructor(public step: Step,
	            public alter: Alter,
	            public octave: Octave) {
	}

	shift(halfTones: number): Pitch {
		return Pitch.fromMidiNoteNumber(this.toMidiNoteNumber() + halfTones)
	}

	static fromMidiNoteNumber(noteNumber: number): Pitch {
		let octave = Math.floor(noteNumber / 12) as Octave
		let step: Step, alter: Alter = 0
		switch (noteNumber % 12) {
			case 0: step = "C"; break
			case 1: step = "C"; alter = 1; break
			case 2: step = "D"; break
			case 3: step = "D"; alter = 1; break
			case 4: step = "E"; break
			case 5: step = "F"; break
			case 6: step = "F"; alter = 1; break
			case 7: step = "G"; break
			case 8: step = "G"; alter = 1; break
			case 9: step = "A"; break
			case 10: step = "A"; alter = 1; break
			case 11: step = "B"; break
		}
		return new Pitch(step, alter, octave)
	}

	toMidiNoteNumber(): number {
		return stepsToMidiNoteNumbers[this.step] + this.alter + 12 * this.octave
	}

	toString(): string {
		return this.step + (this.alter == 1 ? "#" : (this.alter == -1 ? "b" : "")) + this.octave
	}
}

/**
 * Creates a new pitch from the given value like "F4", "Bb5" or "A#3".
 */
export function pitch(pitch: string): Pitch {
	if (pitch.length < 2 || pitch.length > 3)
		throw Error("Invalid pitch: " + pitch)
	const step = pitch.charAt(0) as Step
	const hasAccidental = pitch.length == 3
	let alter = 0 as Alter
	if (hasAccidental) {
		if (pitch.charAt(1) == "#")
			alter = 1
		else if (pitch.charAt(1) == "b")
			alter = -1
		else
			throw Error("Invalid pitch: " + pitch)
	}
	const octave = parseInt(pitch.charAt(hasAccidental ? 2 : 1)) as Octave
	return new Pitch(step, alter, octave)
}

export type Step = "C" | "D" | "E" | "F" | "G" | "A" | "B"
export type Alter = 0 | /* Flat */ -1 | /* Sharp */ 1
export type Octave = 2 | 3 | 4 | 5

/** Fine tuning of a tone: alteration in cents
 * (0 = perfect intonation, -5 = minus 5 cents, +100: one half note up) */
export type Cents = number

/** Time in seconds */
export type Seconds = number

/** Loudness of sound: 0 is silent, 1 is maximum */
export type Loudness = number

export type Instrument = "AltoSax" | "BassClarinet" | "Bassoon" | "Clarinet" |
		"Drums" | "EbClarinet" | "Flute" | "Horn" | "Numbers" | "Oboe" | "Piano" |
		"Trombone" | "Trumpet" | "Tuba" | "TuningFork"

export type ChordPerformanceType = "Synchronous" | "Upwards" | "Downwards"


// WEBPACK FOOTER //
// ./src/model/music.ts